import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getCoordsForZip,
  getInitialLocationFromCookies
} from '../../../ChangeMyLocation/utils'
import {
  MY_LOCATION_COOKIE_UPDATED_EVENT,
  type MyLocationCookieUpdatedDetail
} from '../../../ChangeMyLocation/my-location-cookie-updated-event'
import { PageConfig } from '../../../../../utils/page-config'

export interface MyLocationCoordinates {
  latitude: string | undefined
  longitude: string | undefined
  isLoading: boolean
  error: Error | undefined
  isMyLocationAvailable: boolean
}

/**
 * Whether the interactive "Change My Location" widget is enabled in page config.
 * This gates only the live cookie-update listener, not saved-ZIP availability:
 * a valid us_loc cookie must drive /stores even when disabled.
 */
const isMyLocationWidgetEnabled = (): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fullConfig = PageConfig.getConfig() as any
  const isWidgetEnabled =
    fullConfig?.changeMyLocationConfig?.enableMyLocationWidget === true
  const isGloballyDisabled = fullConfig?.myLocationDisabled === true

  return isWidgetEnabled && !isGloballyDisabled
}

/**
 * Provides lat/long from the user's saved "My Location" (us_loc cookie, US market).
 *
 * - Geocodes the saved ZIP whenever the cookie is present, regardless of whether
 *   the widget is enabled on the page.
 * - Re-geocodes on MY_LOCATION_COOKIE_UPDATED_EVENT, but only when the widget is
 *   enabled (its modal is the only runtime writer of the cookie).
 * - isMyLocationAvailable is true whenever a saved ZIP cookie exists; false when
 *   absent, so callers (useStoreLocation) fall back to IP-based geolocation.
 */
export const useMyLocationCoordinates = (): MyLocationCoordinates => {
  const [coordinates, setCoordinates] = useState<{
    latitude: string | undefined
    longitude: string | undefined
  }>({ latitude: undefined, longitude: undefined })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>(undefined)
  // Whether a saved ZIP cookie exists; seeded synchronously for the first render.
  const [hasSavedZip, setHasSavedZip] = useState<boolean>(() =>
    Boolean(getInitialLocationFromCookies().zipcode)
  )
  const widgetEnabled = isMyLocationWidgetEnabled()
  const geocodeAbortRef = useRef(0)

  const geocodeZip = useCallback(async (zipcode: string) => {
    geocodeAbortRef.current += 1
    const currentRequest = geocodeAbortRef.current
    setIsLoading(true)
    setError(undefined)

    try {
      const result = await getCoordsForZip(zipcode)

      // Only apply if this is still the latest request
      if (currentRequest !== geocodeAbortRef.current) {
        return
      }

      if (result) {
        setCoordinates({
          latitude: result.latitude,
          longitude: result.longitude
        })
      } else {
        setError(new Error(`Failed to geocode zip code: ${zipcode}`))
        setCoordinates({ latitude: undefined, longitude: undefined })
      }
    } catch (err) {
      if (currentRequest !== geocodeAbortRef.current) {
        return
      }
      setError(err as Error)
      setCoordinates({ latitude: undefined, longitude: undefined })
    } finally {
      if (currentRequest === geocodeAbortRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  // Geocode the saved ZIP on mount, independent of the widget flag.
  useEffect(() => {
    const { zipcode } = getInitialLocationFromCookies()
    if (zipcode) {
      setHasSavedZip(true)
      geocodeZip(zipcode)
    } else {
      // No cookie — fall through to IP.
      setHasSavedZip(false)
      setIsLoading(false)
    }
  }, [geocodeZip])

  // Re-geocode on live location changes; only when the widget (cookie writer) is on.
  useEffect(() => {
    if (!widgetEnabled) {
      return undefined
    }

    const handleLocationChange = (event: Event) => {
      const { detail } = event as CustomEvent<MyLocationCookieUpdatedDetail>
      const zipcode = detail?.zipcode

      if (zipcode) {
        setHasSavedZip(true)
        geocodeZip(zipcode)
      } else {
        // Cookie cleared — reset coordinates
        setHasSavedZip(false)
        setCoordinates({ latitude: undefined, longitude: undefined })
      }
    }

    window.addEventListener(
      MY_LOCATION_COOKIE_UPDATED_EVENT,
      handleLocationChange
    )
    return () => {
      window.removeEventListener(
        MY_LOCATION_COOKIE_UPDATED_EVENT,
        handleLocationChange
      )
    }
  }, [widgetEnabled, geocodeZip])

  // Available while a saved ZIP cookie exists (even mid-geocode), so useStoreLocation
  // stays on the My Location branch until it settles instead of racing to IP. A
  // settled error/empty is already treated by useStoreLocation as a fall-through.
  const isMyLocationAvailable = hasSavedZip

  return {
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    isLoading,
    error,
    isMyLocationAvailable
  }
}
